import { NavLink, useLocation } from 'react-router-dom';
import { isItemActive } from '../../utils/menuActivity';
import { TruncatedText } from '@/shared/components/TruncatedText';
import type { SidebarMenuItem } from '../../types';

interface SidebarMenuProps {
  items: readonly SidebarMenuItem[];
  isCollapsed: boolean;
  expandedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
}

export function SidebarMenu({ items, isCollapsed, expandedIds, onToggle }: SidebarMenuProps) {
  return (
    <ul className="sidebar__list">
      {items.map((item) => (
        <MenuItemNode
          key={item.id}
          item={item}
          isCollapsed={isCollapsed}
          expandedIds={expandedIds}
          onToggle={onToggle}
        />
      ))}
    </ul>
  );
}

interface MenuItemNodeProps {
  item: SidebarMenuItem;
  depth?: number;
  isCollapsed: boolean;
  expandedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
}

function MenuIcon({ icon }: { icon?: string }) {
  if (!icon) return null;
  return <i className={`pi ${icon} sidebar__icon`} aria-hidden="true" />;
}

function MenuItemNode({
  item,
  depth = 0,
  isCollapsed,
  expandedIds,
  onToggle,
}: MenuItemNodeProps) {
  const { pathname } = useLocation();
  const children = item.children ?? [];
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(item.id);
  const linkClass = depth > 0 ? 'sidebar__link sidebar__child-link' : 'sidebar__link';

  if (item.disabled) {
    return (
      <li className="sidebar__item">
        <span className={`${linkClass} sidebar__link--disabled`} aria-disabled="true">
          <MenuIcon icon={item.icon} />
          <TruncatedText text={item.label} className="sidebar__label" />
        </span>
      </li>
    );
  }

  if (!hasChildren) {
    return (
      <li className="sidebar__item">
        <NavLink
          to={item.path}
          end={item.path === '/'}
          data-pr-tooltip={isCollapsed ? item.label : undefined}
          className={({ isActive }) =>
            isActive ? `${linkClass} sidebar__link--active` : linkClass
          }
        >
          <MenuIcon icon={item.icon} />
          <TruncatedText text={item.label} className="sidebar__label" />
        </NavLink>
      </li>
    );
  }

  const submenuId = `submenu-${item.id}`;
  const activeChild = isItemActive(item, pathname);

  return (
    <li className="sidebar__item">
      <button
        type="button"
        className={`sidebar__link sidebar__link-parent${
          activeChild ? ' sidebar__link-parent--active-child' : ''
        }`}
        aria-expanded={isExpanded}
        aria-controls={submenuId}
        onClick={() => {
          onToggle(item.id);
        }}
        data-pr-tooltip={isCollapsed ? item.label : undefined}
      >
        <MenuIcon icon={item.icon} />
        <TruncatedText text={item.label} className="sidebar__label" />
        <i
          className={`pi pi-chevron-right sidebar__chevron${
            isExpanded ? ' sidebar__chevron--expanded' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {/* Nested menus are hidden entirely when collapsed (section 9.1). */}
      {!isCollapsed && isExpanded ? (
        <ul className="sidebar__subnav" id={submenuId}>
          {children.map((child) => (
            <MenuItemNode
              key={child.id}
              item={child}
              depth={depth + 1}
              isCollapsed={isCollapsed}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
